document.addEventListener("DOMContentLoaded", function () {
  /**
   * Gokwik's sidecart is often rendered inside a Shadow DOM.
   * We use a main observer to find the shadow host, and a nested observer
   * to watch for dynamic content changes inside the shadow root.
   */

  const mainObserver = new MutationObserver((mutations, obs) => {
    const shadowHost = document.querySelector("shadow-dom-container");

    if (shadowHost && shadowHost.shadowRoot) {
      const root = shadowHost.shadowRoot;

      // 1. Inject Styles
      if (!root.querySelector("#gokwik-custom-styles")) {
        applyCustomStyles(root);
      }

      // 2. Initial processing
      injectFreeProductBanner(root);
      injectEmptyCartRecommendations(root);

      // 3. Attach a nested observer to the shadowRoot for dynamic updates
      if (!root._nestedObserverAttached) {
        const nestedObserver = new MutationObserver(() => {
          injectFreeProductBanner(root);
          injectEmptyCartRecommendations(root);
        });
        nestedObserver.observe(root, { childList: true, subtree: true });
        root._nestedObserverAttached = true;
      }
    }
  });

  mainObserver.observe(document.body, { childList: true, subtree: true });
});

/**
 * Injects the custom Free Product banner.
 * Fetches the image URL from a global variable (set via Shopify metafield).
 */
function injectFreeProductBanner(shadowRoot) {
  // Target containers to try (Gokwik occasionally changes selectors)
  const targetSelectors = [".sidecart-footer", ".side-cart-content-wrapper"];
  let targetWrapper = null;
  
  for (const selector of targetSelectors) {
    targetWrapper = shadowRoot.querySelector(selector);
    if (targetWrapper) break;
  }
  
  if (!targetWrapper) return;

  // Prevent duplicate injection
  if (shadowRoot.querySelector("#custom-free-product-banner")) return;

  // Fetch image URL from metafield (with fallback)
  const bannerImageUrl = window.gokwikCartBannerUrl || "https://cdn.shopify.com/s/files/1/0605/7977/6049/files/free-product.png?v=1744336340";

  const newbanner = document.createElement("div");
  newbanner.id = "custom-free-product-banner";
  newbanner.innerHTML = `
  `;

  targetWrapper.append(newbanner);
}

/**
 * Appends Best Sellers recommendations below the default GoKwik empty state
 */
function injectEmptyCartRecommendations(shadowRoot) {
  const emptyStateWrapper = shadowRoot.querySelector("div.sidecart-empty");
  if (!emptyStateWrapper) return; // Cart is not empty

  // Prevent duplicate injection
  if (shadowRoot.querySelector("#gokwik-empty-recs-container")) return;

  const scriptTag = document.getElementById("gokwik-empty-cart-recommendations");
  let products = [];
  if (scriptTag) {
    try {
      products = JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error(e);
    }
  }

  if (products.length === 0) return;

  const recsContainer = document.createElement("div");
  recsContainer.id = "gokwik-empty-recs-container";
  recsContainer.className = "custom-recs-wrapper";

  let cardsHtml = '';
  products.forEach(p => {
    let compareHtml = '';
    let discountHtml = '';
    
    if (p.compare_at_price && p.compare_at_price_raw > p.price_raw) {
      compareHtml = `<span class="custom-rec-compare">${p.compare_at_price}</span>`;
      const discount = Math.round(((p.compare_at_price_raw - p.price_raw) / p.compare_at_price_raw) * 100);
      discountHtml = `<span class="custom-rec-discount">(${discount}% OFF)</span>`;
    }
    
    // We parse inventory back to a JSON string so it works in the attribute
    const inventoryAttr = typeof p.inventory === 'string' ? p.inventory : JSON.stringify(p.inventory).replace(/"/g, '&quot;');
    
    // Check initial wishlist state
    const isWishlisted = window.Wishlist && window.Wishlist.hasItem(String(p.id));
    const activeClass = isWishlisted ? 'active' : '';
    
    cardsHtml += `
      <div class="custom-rec-item">
        <a href="${p.url}" class="custom-rec-img-wrap">
          <img src="${p.image}" alt="${p.title}" loading="lazy">
        </a>
        <div class="custom-rec-info">
          <a href="${p.url}" class="custom-rec-title">${p.title}</a>
          <div class="custom-rec-prices">
            <span class="custom-rec-price">${p.price}</span>
            ${compareHtml}
            ${discountHtml}
          </div>
        </div>
        <div class="custom-rec-actions">
          <button type="button" class="custom-rec-wishlist ${activeClass}" aria-label="Add to wishlist" data-product-id="${p.product_id}" data-variant-id="${p.id}"
            onclick="if(window.Wishlist) { window.Wishlist.toggleItem('${p.product_id}', '${p.id}'); this.classList.toggle('active'); } else { alert('Wishlist is still loading...'); }">
            <svg viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </button>
          <button type="button" class="custom-rec-quickview"
            data-product-id="${p.product_id}"
            data-product-url="${p.url}"
            data-product-title="${p.title}"
            data-product-price="${p.price_raw}"
            data-product-compare-price="${p.compare_at_price_raw || p.price_raw}"
            data-product-image="${p.image}"
            data-product-variant-id="${p.id}"
            data-product-inventory="${inventoryAttr}"
            onclick="window.openQuickView && window.openQuickView(this)">
            Quick View
          </button>
        </div>
      </div>
    `;
  });

  recsContainer.innerHTML = `
    <div class="custom-recs-header">
      <h3>Best Sellers</h3>
      <a href="/collections/all" class="custom-recs-link">Explore more &rsaquo;</a>
    </div>
    <div class="custom-recs-grid">
      ${cardsHtml}
    </div>
  `;

  // Append inside the empty state wrapper. 
  // This guarantees that when the cart is no longer empty and GoKwik hides or removes this wrapper, 
  // our product cards automatically disappear with it!
  emptyStateWrapper.appendChild(recsContainer);
}

/**
 * Injects custom CSS rules into the Gokwik shadow root.
 */
function applyCustomStyles(shadowRoot) {
  const style = document.createElement("style");
  style.id = "gokwik-custom-styles";

  style.textContent = `
    .sidecart .free-product {
      background-color: #fff9f1 !important;
    }
    .free-product-image {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      gap: 8px !important;
    }
    .free-product-image img {
      width: 100% !important;
      height: auto !important;
    }
    
    
    .scroll-container {
      overflow-x: auto !important;
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;
    }
    .scroll-container::-webkit-scrollbar {
      display: none !important;
    }

    .scroll-container .card {
      min-width:7rem !important;
    }

      .scroll-container .card .product-name{
      max-width:8rem !important;
      font-size:12px !important;
      }

      .scroll-container .card .price{
      display:flex !important;
      flex-direction:column !important;
      gap:3px !important;
    }

       .scroll-container .card .product-image{
       min-height:100% !important;
    }

     .scroll-container .card .discount-banner {
     display:none !important;
    }

      .scroll-container .card .price > br{
      display:none !important;
    }

      .scroll-container .card .price > br{
      display:none !important;
    }

      .scroll-container .card .price > span{
      order:-1 !important;
    }

    .scroll-container .card .add-btn {
       width:100% !important;
      font-size: 0 !important; 
      background-color: #000 !important;
      color:white !important;
      border-color:black !important;
    }

    .add-to-cart{
    background-color: #000 !important;
      color:white !important;
      border-color:black !important;
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
    }

    .scroll-container .card .add-btn::after {
       content:'Add to Cart' !important;
       font-size: 12px !important;
    }

    .offer-box {
    display:none !important;
    }
        
    /* --- Move default empty state to top --- */
    .sidecart-empty {
      height: auto !important;
      min-height: 0 !important;
      flex: 0 0 auto !important;
      padding: 80px 20px 0 !important;
      margin-bottom: 0 !important;
    }

    /* --- Custom Recs --- */
    .custom-recs-wrapper {
      padding: 80px 0 20px;
      width: 100%;
      text-align: left;
    }
    .custom-recs-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    }
    .custom-recs-header h3 {
      font-size: 18px;
      font-weight: 600;
      color: #0b0b0b;
      margin: 0;
    }
    .custom-recs-link {
      font-size: 13px;
      font-weight: 500;
      color: #333;
      text-decoration: none;
      white-space: nowrap;
    }
    .custom-recs-link:hover {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
    .custom-recs-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
    .custom-rec-item {
      background: #ffffff;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      text-align: left;
      border: 1px solid #e8e8e8;
    }
    .custom-rec-img-wrap {
      display: block;
      width: 100%;
      aspect-ratio: 4 / 5;
      overflow: hidden;
      position: relative;
    }
    .custom-rec-img-wrap img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top;
    }
    .custom-rec-info {
      padding: 12px 12px 0;
      background: #fff;
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    .custom-rec-title {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 8px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.4;
      text-decoration: none;
    }
    .custom-rec-prices {
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }
    .custom-rec-price {
      font-size: 16px;
      font-weight: 700;
      color: #000;
    }
    .custom-rec-compare {
      font-size: 14px;
      color: #888;
      text-decoration: line-through;
    }
    .custom-rec-discount {
      font-size: 13px;
      color: #ff3b30;
      font-weight: 600;
    }
    .custom-rec-actions {
      display: flex;
      border-top: 1px solid #e8e8e8;
      height: 44px;
      margin-top: auto;
    }
    .custom-rec-wishlist {
      background: #fff;
      border: none;
      border-right: 1px solid #e8e8e8;
      width: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .custom-rec-wishlist svg {
      width: 20px;
      height: 20px;
      stroke: #333;
      stroke-width: 2;
      fill: none;
      transition: all 0.3s ease;
    }
    .custom-rec-wishlist.active svg {
      fill: #ff3e00;
      stroke: #ff3e00;
    }
    .custom-rec-quickview {
      background: #fff;
      border: none;
      flex: 1;
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      cursor: pointer;
      text-align: center;
      transition: background 0.2s;
    }
    .custom-rec-quickview:hover {
      background: #f7f7f7;
    }

  `;

  shadowRoot.appendChild(style);
}